/**
 * HustleVoiceSettingsPage.jsx — user-facing wake phrase settings.
 *
 * What users can do here:
 *   • See all default wake phrases (Hey Hustle, Hustle Eats, …)
 *   • Replace any phrase with their own (AI validated, profanity / clash blocked)
 *   • Toggle Background Listen (Android only; iOS routes via Siri Shortcuts)
 *   • Open the Apple Shortcuts deep-link to add Hustle Voice shortcuts
 *
 * Admin equivalent (full CRUD) lives at /admin/voice/wake-phrases.
 */
import { useEffect, useState } from "react";
import { API_BASE } from "../lib/api";
import { toast } from "sonner";
import { startWakeWord, stopWakeWord, refreshWakePhrases, getWakeWordState } from "../lib/wakeWord";

const LOCAL_TOGGLE_KEY = "hustle_wake_word_enabled";

export default function HustleVoiceSettingsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [draftPhrase, setDraftPhrase] = useState("");
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [bgEnabled, setBgEnabled] = useState(false);
  const [engine, setEngine] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/voice/wake-phrases`, { credentials: "include" });
      const j = await r.json();
      setItems(j.items || []);
    } catch (e) {
      toast.error("Failed to load wake phrases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    try {
      setBgEnabled(localStorage.getItem(LOCAL_TOGGLE_KEY) === "1");
    } catch {}
    setEngine(getWakeWordState().engine);
  }, []);

  const validatePhrase = async (slug, phrase) => {
    setValidating(true);
    setValidation(null);
    try {
      const r = await fetch(`${API_BASE}/voice/wake-phrases/validate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phrase, slug }),
      });
      const j = await r.json();
      setValidation(j);
      return j;
    } catch (e) {
      setValidation({ ok: false, reasons: ["Validation request failed"] });
      return null;
    } finally {
      setValidating(false);
    }
  };

  const saveOverride = async (slug) => {
    const v = await validatePhrase(slug, draftPhrase);
    if (!v || !v.ok) {
      toast.error("Phrase rejected — see reasons below.");
      return;
    }
    const r = await fetch(`${API_BASE}/voice/wake-phrases/me`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug, phrase: draftPhrase }),
    });
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      toast.error(e?.detail?.reasons?.[0] || "Could not save override");
      return;
    }
    toast.success(`Saved — "${draftPhrase}" now triggers ${slug}.`);
    setEditing(null);
    setDraftPhrase("");
    setValidation(null);
    await load();
    await refreshWakePhrases();
  };

  const removeOverride = async (slug) => {
    const r = await fetch(`${API_BASE}/voice/wake-phrases/me`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug, remove: true }),
    });
    if (!r.ok) {
      toast.error("Could not reset to default");
      return;
    }
    toast.success("Reset to default phrase");
    await load();
    await refreshWakePhrases();
  };

  const toggleBackground = async () => {
    const next = !bgEnabled;
    setBgEnabled(next);
    try { localStorage.setItem(LOCAL_TOGGLE_KEY, next ? "1" : "0"); } catch {}
    if (next) {
      const e = await startWakeWord({
        onWake: (match) => toast(`Wake: ${match.phrase}`, {
          description: `→ ${match.deep_link}`,
        }),
      });
      setEngine(e);
      if (!e) {
        toast.error("No wake-word engine available — try Chrome browser, or install Hustle for Android.");
      } else {
        toast.success(`Listening (engine: ${e})`);
      }
    } else {
      await stopWakeWord();
      setEngine(null);
      toast("Background listen disabled");
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 px-4 py-8" data-testid="hustle-voice-settings">
      <div className="max-w-3xl mx-auto">
        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Hustle Voice settings</h1>
          <p className="text-slate-400 mt-2">
            Customise the wake phrases used to summon Hustle. Try saying any phrase below while the app is open.
          </p>
        </header>

        {/* Background listen toggle */}
        <section className="border border-slate-800 rounded-xl p-5 mb-6 bg-slate-900/40">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Voice activation</h2>
              <p className="text-sm text-slate-400 mt-1 max-w-xl">
                Hustle is voice-controlled by your phone&apos;s built-in assistant — no extra setup required.
                On iPhone, say <em>&ldquo;Hey Siri, Hustle Eats&rdquo;</em> anywhere.
                On Android, say <em>&ldquo;Hey Google, Hustle Eats&rdquo;</em>.
                The optional toggle below also turns on in-app listening while the Hustle tab is open in your browser.
              </p>
            </div>
            <button
              data-testid="toggle-background-listen"
              onClick={toggleBackground}
              className={`px-4 py-2 rounded-full text-sm font-semibold shrink-0 ${
                bgEnabled
                  ? "bg-amber-400 text-slate-900"
                  : "bg-slate-800 text-slate-300 hover:bg-slate-700"
              }`}>
              {bgEnabled ? "Listening in this tab" : "Enable in this tab"}
            </button>
          </div>
          {engine && (
            <p className="text-xs text-slate-500 mt-3">Engine: <span className="text-amber-300">{engine}</span></p>
          )}
        </section>

        {/* Phrase list */}
        <section className="space-y-3">
          {loading && <p className="text-slate-400">Loading…</p>}
          {!loading && items.map((it) => (
            <div key={it.slug} className="border border-slate-800 rounded-xl p-4 bg-slate-900/30"
                 data-testid={`wake-row-${it.slug}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-lg font-semibold">&ldquo;{it.phrase}&rdquo;</span>
                    {it.is_custom && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 uppercase">Custom</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-400 mt-1">{it.description}</p>
                  <p className="text-xs text-slate-500 mt-1 font-mono">{it.deep_link}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {it.is_custom && (
                    <button
                      data-testid={`reset-${it.slug}`}
                      onClick={() => removeOverride(it.slug)}
                      className="text-xs px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700">
                      Reset
                    </button>
                  )}
                  <button
                    data-testid={`edit-${it.slug}`}
                    onClick={() => {
                      setEditing(it.slug);
                      setDraftPhrase(it.phrase);
                      setValidation(null);
                    }}
                    className="text-xs px-3 py-1.5 rounded bg-amber-400 text-slate-900 font-semibold">
                    Customise
                  </button>
                </div>
              </div>

              {editing === it.slug && (
                <div className="mt-4 pt-4 border-t border-slate-800">
                  <label className="text-xs uppercase tracking-wide text-slate-500">Your phrase</label>
                  <input
                    data-testid={`input-${it.slug}`}
                    value={draftPhrase}
                    onChange={(e) => { setDraftPhrase(e.target.value); setValidation(null); }}
                    placeholder="Say something memorable…"
                    className="w-full mt-1 bg-slate-950 border border-slate-700 rounded px-3 py-2 text-base focus:outline-none focus:border-amber-400"
                  />
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      data-testid={`validate-${it.slug}`}
                      onClick={() => validatePhrase(it.slug, draftPhrase)}
                      disabled={validating || !draftPhrase.trim()}
                      className="text-xs px-3 py-2 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-50">
                      {validating ? "Checking…" : "Check phrase"}
                    </button>
                    <button
                      data-testid={`save-${it.slug}`}
                      onClick={() => saveOverride(it.slug)}
                      disabled={validating || !draftPhrase.trim()}
                      className="text-xs px-3 py-2 rounded bg-amber-400 text-slate-900 font-semibold disabled:opacity-50">
                      Save
                    </button>
                    <button
                      onClick={() => { setEditing(null); setValidation(null); }}
                      className="text-xs px-3 py-2 rounded text-slate-400 hover:text-slate-200">
                      Cancel
                    </button>
                  </div>
                  {validation && (
                    <div className={`mt-3 text-xs ${validation.ok ? "text-emerald-400" : "text-rose-400"}`}>
                      {validation.ok ? (
                        <p>Phrase looks good — safe to save.</p>
                      ) : (
                        <>
                          <p className="font-semibold mb-1">Rejected:</p>
                          <ul className="list-disc list-inside space-y-0.5">
                            {(validation.reasons || []).map((r, i) => <li key={i}>{r}</li>)}
                          </ul>
                          {(validation.suggestions || []).length > 0 && (
                            <p className="mt-1 text-slate-400">
                              💡 {validation.suggestions[0]}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </section>

        <footer className="mt-10 p-5 border border-slate-800 rounded-xl bg-slate-900/20 text-sm text-slate-400">
          <h3 className="font-semibold text-slate-200 mb-2">Privacy</h3>
          <p>
            On iPhone, voice detection is handled by Apple Siri.
            On Android, voice detection is handled by Google Assistant (or Samsung Bixby on Samsung devices).
            Only the matched command is sent to Hustle — no raw audio ever reaches our servers.
            In-browser listening uses Web Speech API and stops the moment you close this tab.
          </p>
        </footer>
      </div>
    </div>
  );
}
